import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { cx } from "./index";

export interface Toast {
  message: string;
  tone?: "info" | "error";
  /**
   * An optional follow-up: a link ("Review in Approvals", a plain href so it can leave the
   * router) or a button ("Undo") that runs and then dismisses the toast.
   */
  action?: { label: string; href: string } | { label: string; onClick: () => void };
}

type Shown = Toast & { id: number };

const ToastContext = createContext<((toast: Toast) => void) | null>(null);
const DISMISS_MS = 7000;

/**
 * Short confirmations ("Task deleted", "Waiting for approval"). The live region is always in the
 * page, so screen readers announce each toast as it is added; errors use an assertive region.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Shown[]>([]);
  const next = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    setToasts((all) => all.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer !== undefined) clearTimeout(timer);
    timers.current.delete(id);
  }, []);

  const show = useCallback(
    (toast: Toast) => {
      const id = next.current++;
      setToasts((all) => [...all.slice(-2), { ...toast, id }]);
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), DISMISS_MS),
      );
    },
    [dismiss],
  );

  useEffect(() => {
    const all = timers.current;
    return () => all.forEach((timer) => clearTimeout(timer));
  }, []);

  const value = useMemo(() => show, [show]);
  const polite = toasts.filter((t) => t.tone !== "error");
  const assertive = toasts.filter((t) => t.tone === "error");

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-2 p-4 sm:items-end">
        <div aria-live="polite" aria-atomic="false" className="flex w-full flex-col items-center gap-2 sm:items-end">
          {polite.map((t) => (
            <ToastCard key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
          ))}
        </div>
        <div aria-live="assertive" className="flex w-full flex-col items-center gap-2 sm:items-end">
          {assertive.map((t) => (
            <ToastCard key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
          ))}
        </div>
      </div>
    </ToastContext.Provider>
  );
}

function ToastCard({ toast, onDismiss }: { toast: Shown; onDismiss: () => void }) {
  return (
    <div
      className={cx(
        "pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-lg border border-border bg-surface p-3 text-sm shadow-lg",
        toast.tone === "error" ? "text-danger" : "text-text",
      )}
    >
      <p className="flex-1">
        {toast.message}
        {toast.action === undefined ? null : (
          <>
            {" "}
            <ToastAction action={toast.action} onDismiss={onDismiss} />
          </>
        )}
      </p>
      <button type="button" onClick={onDismiss} className="rounded px-1 text-muted hover:bg-surface-2">
        <span aria-hidden="true">×</span>
        <span className="sr-only">Dismiss</span>
      </button>
    </div>
  );
}

function ToastAction({ action, onDismiss }: { action: NonNullable<Toast["action"]>; onDismiss: () => void }) {
  if ("href" in action) {
    return (
      <a href={action.href} className="font-medium text-accent underline">
        {action.label}
      </a>
    );
  }
  return (
    <button
      type="button"
      onClick={() => {
        action.onClick();
        onDismiss();
      }}
      className="font-medium text-accent underline"
    >
      {action.label}
    </button>
  );
}

export function useToast(): (toast: Toast) => void {
  const show = useContext(ToastContext);
  if (show === null) throw new Error("useToast outside ToastProvider");
  return show;
}
